import { z } from 'zod';
import type { DecisaoParticipacao, ModuloLicitacao, SituacaoArquivo, TipoAtividade } from '../enums/dominios';

// Contratos da área da licitação (RQ-LIC-001 a RQ-LIC-004, RQ-ARQ-001/002/006). Só uuids trafegam.
// Datas em ISO 8601 (UTC). Valores monetários em reais, como número.

export type PessoaResumoDto = { uuid: string; nome: string };

export type EtapaDto = { uuid: string; nome: string; cor: string; icone: string };

export type ProcessoDetalheDto = {
  uuid: string;
  numero: string;
  objeto: string;
  orgao: { uuid: string; nome: string } | null;
  estadoUuid: string;
  responsavel: PessoaResumoDto | null;
  decisaoParticipacao: DecisaoParticipacao;
  modalidade: string | null;
  criterioJulgamento: string | null;
  numeroProcesso: string | null;
  valorEstimado: number | null;
  linkPortal: string | null;
  versao: number;
};

/** Pessoa da licitação: usuário do sistema ou contato externo (do órgão, por exemplo). */
export type PessoaLicitacaoDto = {
  uuid: string;
  nome: string;
  papel: string;
  externa: boolean;
  /** Uuid do usuário quando a pessoa é interna. */
  usuarioUuid: string | null;
  email: string | null;
  telefone: string | null;
  avatarUrl: string | null;
};

export type DocumentoLicitacaoDto = {
  uuid: string;
  nome: string;
  /** Número da versão atual. */
  versao: number;
  arquivoUuid: string;
  mime: string;
  extensao: string;
  tamanhoBytes: number;
  situacao: SituacaoArquivo;
  enviadoEm: string;
  enviadoPor: PessoaResumoDto | null;
};

export type AtividadeDto = {
  uuid: string;
  tipo: TipoAtividade;
  titulo: string;
  responsavel: PessoaResumoDto | null;
  prazo: string | null;
  concluidaEm: string | null;
  versao: number;
};

/** Linha do Histórico (RQ-LIC-004): o que aconteceu, montado das mudanças de estado, envios e conclusões. */
export type EventoHistoricoDto = {
  tipo: 'estado' | 'documento' | 'atividade';
  descricao: string;
  usuario: PessoaResumoDto | null;
  ocorridoEm: string;
};

/** Posição de um módulo no quadro da licitação (arranjo compartilhado, RQ-LIC-001). */
export type ModuloLayoutDto = { modulo: ModuloLicitacao; x: number; y: number; largura: number; altura: number; versao: number };

export type LicitacaoDto = {
  processo: ProcessoDetalheDto;
  /** Estados de fluxo do quadro, na ordem (trilha de etapas do "Sobre"). */
  etapas: EtapaDto[];
  pessoas: PessoaLicitacaoDto[];
  documentos: DocumentoLicitacaoDto[];
  /** Atividades, marcos e entregas, por prazo (sem prazo por último). */
  atividades: AtividadeDto[];
  /** Mais recente primeiro. */
  historico: EventoHistoricoDto[];
  /** Só os módulos já movidos; os demais usam a posição padrão do front. */
  layout: ModuloLayoutDto[];
  /** Pessoas do quadro que podem ser adicionadas ou ser responsáveis. */
  membrosQuadro: PessoaResumoDto[];
  limiteArquivoMb: number;
};

const textoOpcional = (max: number) => z.string().trim().max(max).nullable().optional();

/** Campos do "Sobre". Só os informados mudam; null limpa o campo opcional. */
export const esquemaEditarProcesso = z.object({
  versao: z.number().int().positive(),
  numero: z.string().trim().min(1).max(40).optional(),
  objeto: z.string().trim().min(1).max(500).optional(),
  modalidade: textoOpcional(120),
  criterioJulgamento: textoOpcional(120),
  numeroProcesso: textoOpcional(60),
  valorEstimado: z.number().finite().min(0).max(1e13).nullable().optional(),
  linkPortal: z.string().trim().url().max(1000).nullable().optional(),
});
export type EditarProcessoEntrada = z.infer<typeof esquemaEditarProcesso>;

const dataIso = z.string().datetime({ offset: true });

export const esquemaCriarAtividade = z.object({
  tipo: z.number().int().min(1).max(3),
  titulo: z.string().trim().min(1).max(200),
  responsavelUuid: z.string().uuid().nullable().optional(),
  prazo: dataIso.nullable().optional(),
});
export type CriarAtividadeEntrada = z.infer<typeof esquemaCriarAtividade>;

export const esquemaAlterarAtividade = z.object({
  versao: z.number().int().positive(),
  titulo: z.string().trim().min(1).max(200).optional(),
  responsavelUuid: z.string().uuid().nullable().optional(),
  prazo: dataIso.nullable().optional(),
  concluida: z.boolean().optional(),
});
export type AlterarAtividadeEntrada = z.infer<typeof esquemaAlterarAtividade>;

/** Adiciona um usuário do quadro ou um contato externo novo (exatamente um dos dois). */
export const esquemaAdicionarPessoa = z
  .object({
    papel: z.string().trim().min(1).max(120),
    usuarioUuid: z.string().uuid().optional(),
    contato: z
      .object({
        nome: z.string().trim().min(1).max(200),
        email: z.string().trim().email().max(320).nullable().optional(),
        telefone: z.string().trim().max(40).nullable().optional(),
      })
      .optional(),
  })
  .refine((p) => !!p.usuarioUuid !== !!p.contato, { message: 'Informe o usuário ou o contato.' });
export type AdicionarPessoaEntrada = z.infer<typeof esquemaAdicionarPessoa>;

const coordenadaModulo = z.number().finite().min(-20000).max(20000);

/** Novas posições de módulos; versao 0 = módulo ainda na posição padrão (cria o registro). */
export const esquemaAlterarLayout = z.object({
  modulos: z
    .array(
      z.object({
        modulo: z.number().int().min(1).max(7),
        x: coordenadaModulo,
        y: coordenadaModulo,
        largura: z.number().finite().min(120).max(4000),
        altura: z.number().finite().min(80).max(4000),
        versao: z.number().int().min(0),
      }),
    )
    .min(1)
    .max(7),
});
export type AlterarLayoutEntrada = z.infer<typeof esquemaAlterarLayout>;

export type EnvioDocumentoDto = { documentoUuid: string; arquivoUuid: string };

/** URL assinada de curta duração para ver ou baixar (RQ-ARQ-006). */
export type UrlArquivoDto = { url: string; nome: string; mime: string; expiraEmSegundos: number };
